import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DocumentEditModal({ document, open, onClose, onUpdate }) {
  const [validityStatus, setValidityStatus] = useState(document?.validity_status || 'Valid');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const formData = new FormData();
    formData.append('validity_status', validityStatus);

    try {
      await axios.patch(`${API}/documents/${document.verification_id}/status`, formData);
      toast.success('Document updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete document "${document.document_id}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/documents/${document.verification_id}`);
      toast.success('Document deleted successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Document ID</Label>
            <p className="text-sm font-mono text-slate-700">{document?.document_id}</p>
          </div>

          <div className="space-y-2">
            <Label>Document Name</Label>
            <p className="text-sm text-slate-700">{document?.document_name || 'N/A'}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="validity-status">Validity Status</Label>
            <Select value={validityStatus} onValueChange={setValidityStatus}>
              <SelectTrigger id="validity-status" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Valid">Valid</SelectItem>
                <SelectItem value="Revoked">Revoked</SelectItem>
                <SelectItem value="Invalid">Invalid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-slate-900 hover:bg-slate-800">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              variant="outline"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {deleting ? 'Deleting...' : <Trash2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
